import PropTypes from 'prop-types';
import FormRow from '@/components/core/form/FormRow';
import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { isDefined } from '@/utils/miscUtils';
import { useDerivedParam } from '@/stores/derivedCacheStore';
import { useDerivedValueFetch } from '@/hooks/useDerivedValueFetch';
import ConversionField from '../../ConversionField';
import usePatchParams from '../../usePatchParams';
import ErrorMessage from '../ErrorMessage';
import { useParamsContext } from '@/components/providers/useParamsContext';
import SelectBase from '@/components/core/form/SelectBase';
import { isInteger, isNumber } from '@/utils/miscUtils';
import { DISPLAY_TYPES } from '@/consts';

const setValueAs = v => {
  if (isInteger(v)) {
    return parseInt(v);
  }

  if (isNumber(v)) {
    return parseFloat(v);
  }

  return v;
};

const DerivedSelectField = ({
  param,
  paramSchema,
  name,
  parentName,
  onChange,
  displayType = DISPLAY_TYPES.row,
  isGroupField = false,
}) => {
  const { label, tooltip, source, convertible_units: convertibleUnits, allow_null: allowNull, editable } = paramSchema;

  const { primary: { value, is_default: isDefault } = {}, unit, options = [] } = param ?? {};
  const { fetch, changed_dependencies: changedDependencies } = value ?? {};

  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const errorMessage = isGroupField
    ? errors?.[parentName]?.message?.[paramSchema.name]?.primary?.value
    : errors?.[name];

  const { readOnly } = useParamsContext();
  const disabled = !editable || readOnly;

  const conversionName = parentName ? `${parentName}.conversion.${name}` : `conversion.${name}`;
  const { patchPrimary } = usePatchParams();

  const results = useDerivedParam(name);
  const shouldFetch = fetch || !results;
  const currentValue = results?.scalar ?? results ?? '';

  const { isLoading } = useDerivedValueFetch({
    name,
    shouldFetch,
    changedDependencies,
  });

  const registration = register(name, {
    setValueAs,
    disabled,
  });

  const handleChange = async e => {
    if (disabled) return;

    const convertedValue = setValueAs(e.target.value);

    if (isGroupField && onChange) {
      setValue(name, convertedValue, { shouldValidate: true, shouldDirty: true });
      registration.onChange(e);
      onChange(e);
    } else {
      await patchPrimary(name, convertedValue);
    }
  };

  useEffect(() => {
    const value = results?.scalar ?? results;

    if (isDefined(value)) {
      setValue(name, value, { shouldValidate: true, shouldDirty: false });
    }
  }, [results, name, setValue]);

  if (!isDefined(results) && !isLoading) {
    return null;
  }

  const selectProps = {
    name,
    allowNull,
    hasError: !!errorMessage,
    isDefault: isDefault,
    value: currentValue,
    options: results?.options || options,
    disabled,
    onChange: handleChange,
  };

  const conversionField = convertibleUnits?.length > 0 && (
    <ConversionField
      className={displayType === DISPLAY_TYPES.table ? 'w-full' : ''}
      unit={isGroupField ? unit : { value: results?.unit }}
      name={conversionName}
      paramName={name}
      units={convertibleUnits}
      disabled={disabled}
      readOnly={isGroupField ? readOnly : disabled}
    />
  );

  if (displayType === DISPLAY_TYPES.table) {
    return (
      <>
        <tr>
          <td className="text-sm p-2 pl-4">{label}</td>
          <td className="p-2">
            <SelectBase {...registration} {...selectProps} />
          </td>
          <td className="p-2 pr-4 w-full">{conversionField}</td>
        </tr>
        {errorMessage && (
          <tr>
            <td></td>
            <td className="p-0 pl-1">
              <ErrorMessage text={errorMessage} className="p-0" />
            </td>
            <td></td>
          </tr>
        )}
      </>
    );
  }

  return (
    <FormRow label={label} tooltip={tooltip} source={source} name={name}>
      <div className="flex flex-col flex-1">
        <div className={`${convertibleUnits?.length > 0 ? 'join flex' : ''}`}>
          <SelectBase {...registration} {...selectProps} />
          {conversionField}
        </div>
        <ErrorMessage text={errorMessage} />
      </div>
    </FormRow>
  );
};

DerivedSelectField.propTypes = {
  param: PropTypes.object,
  paramSchema: PropTypes.object,
  name: PropTypes.string,
  parentName: PropTypes.string,
  onChange: PropTypes.func,
  displayType: PropTypes.oneOf(['row', 'table']),
  isGroupField: PropTypes.bool,
  children: PropTypes.node,
};

export default DerivedSelectField;
